import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';

import { LAYOUT_CONFIG } from '@pt/services/layout/layout.config';
import { isLayoutId, LayoutId } from '@pt/services/layout/layout.types';

import { DefaultLayoutComponent } from './layouts/default/default-layout.component';
import { EmptyLayoutComponent } from './layouts/empty/empty-layout.component';

@Component({
  selector: 'app-layout',
  imports: [DefaultLayoutComponent, EmptyLayoutComponent],
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex min-w-0 max-w-full flex-1',
  },
})
/**
 * Resolves and renders the active layout using global defaults and route data overrides.
 */
export class LayoutComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly layoutConfig = inject(LAYOUT_CONFIG);

  private readonly activeLayoutState = signal<LayoutId>(this.layoutConfig.defaultLayout);

  /** Active layout selected for the current route branch. */
  readonly activeLayout = computed(() => this.activeLayoutState());

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.resolveActiveLayout()),
        takeUntilDestroyed(),
      )
      .subscribe((layoutId) => this.activeLayoutState.set(layoutId));
  }

  /**
   * Resolves the active layout by starting from default config and
   * applying the closest route branch override from `data.layout`.
   */
  private resolveActiveLayout(): LayoutId {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    let resolvedLayout = this.layoutConfig.defaultLayout;

    for (const routeFromRoot of route.pathFromRoot) {
      const layoutFromRouteData = routeFromRoot.routeConfig?.data?.['layout'];

      if (isLayoutId(layoutFromRouteData)) {
        resolvedLayout = layoutFromRouteData;
      }
    }

    return resolvedLayout;
  }
}
